import { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import { useOutletContext } from "react-router-dom";
import { Box, Button, Card, styled, Typography, useTheme } from "@mui/material";
import { Close } from "@mui/icons-material";
import toast from "react-hot-toast";
import { AccountModal, Block } from "./accountPieces";
import { AccountWrap, GradientTop, ModalBox, ModalButtonWrap, SecondaryButton } from "./components/pieces";
import { AccountHeader, CapHeader } from "./components/Typography";
import useMuiTable, { getComparator, stableSort } from "./components/useMuiTable";
import LITableHead from "./components/LITableHead";
import PayoutTableRow from "./PayoutComponents/PayoutTableRow";
import AppPagination from "./components/AppPagination";
import { Axios, buttonGradient, formatMoney } from "../../common/utils";
import { baseApi } from "../../common/constants";
import PayoutTableRowMobile from "./PayoutComponents/PayoutTableRowMobile";
import type { Size, Payout, AppContext, Config, HeadCell } from "../../types";

const headCells: HeadCell[] = [
    {
        id: `Payout Date`,
        numeric: false,
        disablePadding: false,
        align: `left`,
        label: `Payout Date`,
        sx: { maxWidth: 80, padding: `15px 0 15px 28px` },
    },
    {
        id: `Payout Type`,
        numeric: false,
        disablePadding: false,
        align: `left`,
        label: `Description`,
        colSpan: 2,
    },
    {
        id: `Payout Amount`,
        numeric: false,
        disablePadding: false,
        align: `right`,
        label: `Amount`,
    },
    {
        id: `Payout Status`,
        numeric: false,
        disablePadding: false,
        align: `center`,
        label: `Status`,
        sx: { "& span": { marginRight: `8px` } },
    },
];

const Heading = styled(Box)(() => ({
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 16,
}));

interface PayoutsProps {
    useMobileTables: boolean;
    editable: boolean;
    context: AppContext;
    setContext: (nv: Partial<Config>) => void;
    refreshContext: () => Promise<void>;
}

const Payouts = ({ useMobileTables, editable, context, setContext, refreshContext }: PayoutsProps) => {
    const [payouts, setPayouts] = useState<Payout[]>([]);

    const { page, order, orderBy, setPage, rowsPerPage, handleChangePage, handleRequestSort, handleChangeRowsPerPage } =
        useMuiTable({
            defaultOrderBy: `date`,
        });
    // rows are sorted by a dynamic orderBy key; bridge Payout to the util's Record constraint through unknown[]
    const sortablePayouts: unknown[] = payouts;
    const sortedPayouts: unknown[] = stableSort(
        sortablePayouts as Record<string, string | number>[],
        getComparator(order, orderBy)
    );
    const filteredPayouts = sortedPayouts as Payout[];
    const theme = useTheme();
    const [size] = useOutletContext<[Size, (open?: unknown) => void]>();
    const [cancelPayout, setCancelPayout] = useState<Payout | null>(null);

    const fetchPayouts = () =>
        Axios.get(`${baseApi}payout`)
            .then(r => {
                setPayouts(r.data.programTransactions);
                setPage(0);
            })
            .catch(() => {
                toast.error(`Sorry something went wrong`);
            });

    const cancelFunc = (v: Payout) => {
        Axios.delete(`${baseApi}cashback?id=${v.cancelId}&offset=${new Date().getTimezoneOffset()}`)
            .then(refreshContext)
            .then(fetchPayouts)
            // setCancelPayout receives the resolved (undefined) value, clearing the modal; cast drops the state-setter arg type at this promise boundary
            .then(setCancelPayout as () => void)
            .catch(() => {
                toast.error(`Sorry something went wrong`);
                setCancelPayout(null);
            });
    };

    useState(() => {
        if (context?.adminConfig!.mall!.CORE_SETTINGS![`Payout Method`] === `Request`) {
            headCells[4] = {
                id: ``,
                numeric: false,
                disablePadding: false,
                align: `center`,
                label: ``,
            };
        }
        fetchPayouts();
    });

    return (
        <Block size={size}>
            {!!cancelPayout && (
                <AccountModal onClose={() => setCancelPayout(null)} size={size} open={!!cancelPayout}>
                    <Card sx={{ boxSizing: `border-box` }} className="modalContent">
                        <Close
                            sx={{
                                position: `absolute`,
                                top: 24,
                                right: 10,
                                zIndex: 30,
                                cursor: `pointer`,
                                opacity: 0.55,
                                height: 25,
                                width: 25,
                                zindex: 30,
                            }}
                            onClick={() => setCancelPayout(null)}
                        />
                        {size !== `small` && <GradientTop />}
                        <ModalBox>
                            <Heading sx={{ marginBottom: 3 }}>Cancel Request</Heading>
                            <Typography sx={{ marginBottom: 6 }}>
                                Cancel your payout for {formatMoney(cancelPayout[`Payout Amount`])}?
                            </Typography>
                            <ModalButtonWrap>
                                <SecondaryButton
                                    onClick={() => {
                                        setCancelPayout(null);
                                    }}
                                >
                                    Keep it
                                </SecondaryButton>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => cancelFunc(cancelPayout)}
                                    sx={{
                                        background: buttonGradient(theme),
                                        padding: `4px 8px`,
                                        minWidth: `unset`,
                                        borderRadius: 2,
                                        "&.Mui-disabled": {
                                            background: `#999 !important`,
                                            opacity: 0.5,
                                        },
                                    }}
                                >
                                    Cancel it
                                </Button>
                            </ModalButtonWrap>
                        </ModalBox>
                    </Card>
                </AccountModal>
            )}
            <AccountWrap size={size}>
                <AccountHeader size={size}>Payout History</AccountHeader>
                <Card
                    sx={{
                        borderRadius: `12px 12px 0 0`,
                        px: 3,
                        background: theme.palette.account.background,
                    }}
                >
                    <CapHeader size={size} py={3} m={0}>
                        PAYOUTS
                    </CapHeader>
                </Card>
                <Card
                    sx={{ borderRadius: `0 0 12px 12px`, px: 3, pb: 3, background: theme.palette.account.background }}
                >
                    <TableContainer sx={{ borderRadius: `16px`, border: `1px solid #E1E1E1`, overflow: `hidden` }}>
                        <Table
                            sx={{
                                border: `none`,
                            }}
                        >
                            <LITableHead
                                rowCount={filteredPayouts.length}
                                onRequestSort={handleRequestSort}
                                {...{ useMobileTables, editable, setContext, order, orderBy, headCells, context }}
                            />
                            <TableBody sx={{ background: `#FFF` }}>
                                {filteredPayouts
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map(payout =>
                                        useMobileTables ? (
                                            <PayoutTableRowMobile
                                                key={payout.id}
                                                onClick={setCancelPayout}
                                                {...{ payout, context }}
                                            />
                                        ) : (
                                            <PayoutTableRow
                                                key={payout.id}
                                                onClick={setCancelPayout}
                                                {...{ payout, context }}
                                            />
                                        )
                                    )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
                <AppPagination
                    page={page}
                    component="div"
                    rowsPerPage={rowsPerPage}
                    count={filteredPayouts.length}
                    onPageChange={handleChangePage}
                    rowsPerPageOptions={[25, 50, 100]}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    slotProps={{ select: { MenuProps: { disableScrollLock: true } } }}
                />
            </AccountWrap>
        </Block>
    );
};

export default Payouts;
